import { useRef, useState, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { Palette, Languages, Check, Wifi, WifiOff, Users, Copy, Lock, Shield, UserCheck, Eye, Sun, Moon } from 'lucide-react';
import { getServerState, startMultiUserServer, stopMultiUserServer, getLocalIP } from '../services/multiUserServer';
import { setServerPIN, isPINEnabled, clearServerPIN, getActiveSessions, disconnectAllClients, setMaxClients, getMaxClients, type UserRole } from '../services/serverAuth';
import { getAvailableLanguages, setLanguage, getLanguage, type Language } from '../lib/i18n';
import { getEyeCareModes, setEyeCareMode, getEyeCareMode, type EyeCareMode } from '../lib/eyeCare';

const APPEARANCE_OPTIONS: { value: 'light' | 'dark' | 'system'; label: string; comingSoon?: boolean }[] = [
  { value: 'light', label: 'Light Mode' },
  { value: 'dark', label: 'Dark Mode', comingSoon: true },
  { value: 'system', label: 'System Mode', comingSoon: true },
];

const LANGUAGE_OPTIONS = getAvailableLanguages();
const EYE_CARE_OPTIONS = getEyeCareModes();

const ROLE_INFO: { value: UserRole; label: string; desc: string; icon: any }[] = [
  { value: 'admin', label: 'Admin', desc: 'Full access — create, edit, delete, reports', icon: Shield },
  { value: 'entry', label: 'Entry', desc: 'Create & edit vouchers, view reports', icon: UserCheck },
  { value: 'viewer', label: 'Viewer', desc: 'View-only — reports and data, no editing', icon: Eye },
];

const ServerSecurityPanel = () => {
  const [pin, setPin] = useState('');
  const [pinSet, setPinSet] = useState(isPINEnabled());
  const [maxCli, setMaxCli] = useState(String(getMaxClients()));
  const [sessions, setSessions] = useState(getActiveSessions());
  const setGlobalMessage = useStore(s => s.setGlobalMessage);

  useEffect(() => {
    const interval = setInterval(() => setSessions(getActiveSessions()), 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSetPIN = () => {
    if (pin.length < 4) { setGlobalMessage('PIN must be at least 4 characters'); return; }
    setServerPIN(pin);
    setPinSet(true);
    setGlobalMessage('Server PIN set successfully');
    setPin('');
  };

  const handleClearPIN = () => {
    clearServerPIN();
    setPinSet(false);
    setGlobalMessage('Server PIN removed — no authentication required');
  };

  const handleMaxClients = () => {
    const n = parseInt(maxCli);
    if (n < 1 || n > 50) { setGlobalMessage('Max clients must be between 1 and 50'); return; }
    setMaxClients(n);
    setGlobalMessage(`Max clients set to ${n}`);
  };

  return (
    <div className="mt-3 space-y-3">
      {/* PIN Setup */}
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="flex items-center gap-2 mb-2 text-[12px] font-bold text-slate-700">
          <Lock className="w-3.5 h-3.5 text-[#1e8585]" />
          Access PIN
          {pinSet && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-[1px] rounded-full">Active</span>}
        </div>
        {!pinSet ? (
          <div className="flex items-center gap-2">
            <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="Set PIN (min 4 chars)"
              className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-[12px] outline-none focus:border-[#1e8585]" />
            <button onClick={handleSetPIN} className="px-3 py-1.5 bg-[#1e8585] text-white text-[11px] font-bold rounded hover:bg-[#166e6e]">Set PIN</button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-500">Clients must enter PIN to connect</span>
            <button onClick={handleClearPIN} className="px-3 py-1 text-[11px] font-bold text-rose-600 border border-rose-200 rounded hover:bg-rose-50">Remove PIN</button>
          </div>
        )}
      </div>

      {/* Max Clients */}
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="flex items-center gap-2 mb-2 text-[12px] font-bold text-slate-700">
          <Users className="w-3.5 h-3.5 text-[#1e8585]" />
          Max Clients
        </div>
        <div className="flex items-center gap-2">
          <input type="number" value={maxCli} onChange={e => setMaxCli(e.target.value)} min="1" max="50"
            className="w-[60px] border border-slate-200 rounded px-2 py-1.5 text-[12px] text-center outline-none focus:border-[#1e8585]" />
          <button onClick={handleMaxClients} className="px-3 py-1.5 text-[11px] font-bold text-[#1e8585] border border-[#1e8585] rounded hover:bg-[#f0f9f9]">Update</button>
          <span className="text-[10px] text-slate-400 ml-2">Limit simultaneous connections</span>
        </div>
      </div>

      {/* Active Sessions */}
      {sessions.length > 0 && (
        <div className="border border-slate-200 rounded-lg p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-bold text-slate-700">Active Sessions ({sessions.length})</span>
            <button onClick={() => { disconnectAllClients(); setSessions([]); setGlobalMessage('All clients disconnected'); }}
              className="text-[10px] font-bold text-rose-500 hover:text-rose-700">Disconnect All</button>
          </div>
          <div className="space-y-1">
            {sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-[1px] rounded text-[9px] font-bold uppercase ${
                    s.role === 'admin' ? 'bg-purple-100 text-purple-700' : s.role === 'entry' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>{s.role}</span>
                  <span className="text-slate-600 font-mono">{s.clientIP}</span>
                </div>
                <span className="text-slate-400">{s.token}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Role Legend */}
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="text-[12px] font-bold text-slate-700 mb-2">Access Roles</div>
        <div className="space-y-1.5">
          {ROLE_INFO.map(r => {
            const Icon = r.icon;
            return (
              <div key={r.value} className="flex items-center gap-2 text-[11px]">
                <Icon className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-bold text-slate-700 w-[50px]">{r.label}</span>
                <span className="text-slate-400">—</span>
                <span className="text-slate-500">{r.desc}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MultiUserSection = () => {
  const [server, setServer] = useState(getServerState());
  const [localIP, setLocalIP] = useState('');
  const [manualIP, setManualIP] = useState('');
  const [copied, setCopied] = useState(false);
  const setGlobalMessage = useStore(s => s.setGlobalMessage);

  useEffect(() => {
    getLocalIP().then(ip => setLocalIP(ip));
  }, []);

  const displayIP = manualIP.trim() || localIP || '';

  useEffect(() => {
    const interval = setInterval(() => setServer(getServerState()), 2000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = async () => {
    if (server.isRunning) {
      const result = stopMultiUserServer();
      setGlobalMessage(result.message);
    } else {
      const result = await startMultiUserServer();
      setGlobalMessage(result.message);
    }
    setServer(getServerState());
  };

  const connectionURL = displayIP ? `http://${displayIP}:${server.port}` : `http://localhost:${server.port}`;

  const copyURL = () => {
    navigator.clipboard.writeText(connectionURL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold">
        <Users className="w-4 h-4 text-[#1e8585]" />
        Multi-User Server
      </div>

      <div className={`border rounded-lg p-4 transition-colors ${server.isRunning ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {server.isRunning ? <Wifi className="w-4 h-4 text-emerald-600" /> : <WifiOff className="w-4 h-4 text-slate-400" />}
            <span className={`text-[13px] font-bold ${server.isRunning ? 'text-emerald-700' : 'text-slate-600'}`}>
              {server.isRunning ? 'Server Running' : 'Server Off'}
            </span>
          </div>
          <button
            onClick={handleToggle}
            className={`px-4 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${
              server.isRunning
                ? 'bg-rose-500 text-white hover:bg-rose-600'
                : 'bg-[#1e8585] text-white hover:bg-[#166e6e]'
            }`}
          >
            {server.isRunning ? 'Stop Server' : 'Start Server'}
          </button>
        </div>

        {server.isRunning && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-[11px] text-slate-500 font-semibold">Client URL:</span>
              <code className="text-[12px] font-bold text-[#1e8585] flex-1 font-mono">{connectionURL}</code>
              <button onClick={copyURL} className="text-slate-400 hover:text-[#1e8585] p-1" title="Copy URL">
                <Copy className="w-3.5 h-3.5" />
              </button>
              {copied && <span className="text-[10px] text-emerald-600 font-bold">Copied!</span>}
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <span className="text-slate-500">Connected Clients: <strong className="text-slate-800">{server.connectedClients}</strong></span>
              <span className="text-slate-500">Port: <strong className="text-slate-800">{server.port}</strong></span>
            </div>
            {!localIP && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-[10px] text-amber-700 font-bold mb-1">IP auto-detect nahi ho paya. Manually enter karo:</div>
                <div className="flex items-center gap-2">
                  <input type="text" value={manualIP} onChange={e => setManualIP(e.target.value)}
                    placeholder="e.g. 192.168.1.5"
                    className="flex-1 border border-amber-300 rounded px-2 py-1 text-[12px] font-mono outline-none focus:border-[#1e8585]" />
                </div>
                <div className="text-[9px] text-amber-600 mt-1">
                  IP find karo: Win+R → cmd → type <strong>ipconfig</strong> → "IPv4 Address" dekho
                </div>
              </div>
            )}
          </div>
        )}

        {!server.isRunning && (
          <p className="text-[11px] text-slate-400 mt-1">
            Start the server to allow other PCs on the same WiFi/LAN to access Aarthika Pro from their browser. No internet required.
          </p>
        )}
      </div>

      {/* Security & Access */}
      {server.isRunning && <ServerSecurityPanel />}

      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-[11px] text-amber-800 font-semibold mb-1">How it works:</div>
        <ol className="text-[10.5px] text-amber-700 space-y-1 list-decimal pl-4">
          <li>Click "Start Server" on this PC</li>
          <li>Connect other PCs/tablets to the same WiFi</li>
          <li>Open browser on client PC → type the URL shown above</li>
          <li>Full Aarthika Pro opens in browser — no install needed</li>
        </ol>
      </div>
    </div>
  );
};

export const CustomizationView = ({ handleItemSelect }: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const updateActiveCompanyFields = useStore((state) => state.updateActiveCompanyFields);

  const appearance = activeCompany?.appearance || 'light';
  const language = activeCompany?.language || 'en';

  const quit = () => { if (handleItemSelect) handleItemSelect({ name: 'Quit' }); };

  const setAppearance = async (value: 'light' | 'dark' | 'system', comingSoon?: boolean) => {
    if (comingSoon || !activeCompany?.id) return;
    await db.companies.update(activeCompany.id, { appearance: value });
    updateActiveCompanyFields({ appearance: value });
  };

  const [currentLang, setCurrentLang] = useState(getLanguage());
  const [currentEyeCare, setCurrentEyeCare] = useState(getEyeCareMode());

  const handleLanguageChange = async (value: Language) => {
    if (!activeCompany?.id) return;
    setLanguage(value);
    setCurrentLang(value);
    await db.companies.update(activeCompany.id, { language: value });
    updateActiveCompanyFields({ language: value });
  };

  const handleEyeCareChange = (mode: EyeCareMode) => {
    setEyeCareMode(mode);
    setCurrentEyeCare(mode);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
      <div ref={containerRef} className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
        style={{ width: '560px', maxWidth: '98vw', maxHeight: '94vh' }}>

        <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
          <span className="w-1/3 text-left">Customization</span>
          <span className="w-1/3 text-center uppercase">{activeCompany?.name || ''}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={quit}>✕</span>
        </div>

        <div className="flex-1 p-6 overflow-y-auto bg-[#fdfdfd] space-y-6">
          {/* Appearance */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold">
              <Palette className="w-4 h-4 text-[#1e8585]" />
              Appearance
            </div>
            <div className="grid grid-cols-3 gap-3">
              {APPEARANCE_OPTIONS.map(opt => {
                const isActive = appearance === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAppearance(opt.value, opt.comingSoon)}
                    disabled={opt.comingSoon}
                    className={`relative border rounded-lg px-3 py-3 text-left transition-colors ${
                      isActive ? 'border-[#1e8585] bg-[#f0f9f9] ring-1 ring-[#1e8585]' : 'border-slate-200 hover:bg-slate-50'
                    } ${opt.comingSoon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-700 text-[12.5px]">{opt.label}</span>
                      {isActive && <Check className="w-4 h-4 text-[#1e8585]" />}
                    </div>
                    {opt.comingSoon && (
                      <span className="text-[10px] text-amber-600 font-semibold uppercase tracking-wide">Coming Soon</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Language */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold">
              <Languages className="w-4 h-4 text-[#1e8585]" />
              Language
            </div>
            <div className="grid grid-cols-5 gap-2">
              {LANGUAGE_OPTIONS.map(opt => {
                const isActive = currentLang === opt.code;
                return (
                  <button key={opt.code} type="button" onClick={() => handleLanguageChange(opt.code)}
                    className={`border rounded-lg px-2 py-2.5 text-center transition-colors cursor-pointer ${
                      isActive ? 'border-[#1e8585] bg-[#f0f9f9] ring-1 ring-[#1e8585]' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                    <div className="text-[13px] font-bold text-slate-700">{opt.nativeName}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{opt.name}</div>
                    {isActive && <Check className="w-3 h-3 text-[#1e8585] mx-auto mt-1" />}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              UI labels will change. Reports, invoices &amp; accounting terms remain in English.
            </p>
          </div>

          {/* Eye Care Mode */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold">
              <Sun className="w-4 h-4 text-[#1e8585]" />
              Eye Care Mode
            </div>
            <div className="grid grid-cols-4 gap-2">
              {EYE_CARE_OPTIONS.map(opt => {
                const isActive = currentEyeCare === opt.value;
                return (
                  <button key={opt.value} type="button" onClick={() => handleEyeCareChange(opt.value)}
                    className={`border rounded-lg px-2 py-2.5 text-center transition-colors cursor-pointer ${
                      isActive ? 'border-[#1e8585] bg-[#f0f9f9] ring-1 ring-[#1e8585]' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                    <div className="text-[12px] font-semibold text-slate-700">{opt.label}</div>
                    <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">{opt.desc}</div>
                    {isActive && <Check className="w-3 h-3 text-[#1e8585] mx-auto mt-1" />}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Reduces eye strain during long working hours. Saved automatically.
            </p>
          </div>

          {/* Multi-User Server */}
          <MultiUserSection />
        </div>

        <div className="flex justify-end items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0 h-[33px]">
          <span className="text-gray-500 italic text-[11.5px]">Esc to Close</span>
        </div>
      </div>
    </div>
  );
};
